import React, { useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DocumentTypePropertyGeneralDataCard from './DocumentTypePropertyGeneralDataCard';
import { initialState, reducer } from 'features/documentTypes/reducers/properties/generalData/containerReducer';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { documentTypeXPropertyValidator } from 'features/documentTypes/validators';
import { useToast } from 'hooks/toasts/useToast';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { PROPERTY_DETAILS_QUERY } from '../../queries';
import { PROPERTIES_QUERY } from 'features/properties/components/list/queries';
import { PROPERTY_TYPES_QUERY } from 'features/properties/components/edit/queries';
import { ADD_UPDATE_DOCUMENT_TYPE_PROPERTY } from '../../mutations';
import { REMOVE_DOCUMENT_TYPE_PROPERTY } from '../../../list/mutations';
import { DOCUMENT_TYPE_PROPERTY_UPDATED } from '../../subscriptions';

const DocumentTypePropertyGeneralDataContainer = ({ documentTypeId, propertyId, data, onChangeDocumentTypePropertyTitle, onRedirect }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);

  const [documentTypeXPropertyValidation, validateDocumentTypeXProperty] = useDirtyFieldValidation(documentTypeXPropertyValidator);

  // #region Queries

  const { data: propertiesData, refetch: refetchPropertiesData } = useQuery(PROPERTIES_QUERY);

  const { data: propertyTypesData, refetch: refetchPropertyTypes } = useQuery(PROPERTY_TYPES_QUERY);

  const { data: propertyDetailsData } = useQuery(PROPERTY_DETAILS_QUERY, {
    variables: { propertyId: propertyId },
    skip: propertyId === undefined || propertyId === null
  });

  // #endregion

  // #region Mutations

  const [addOrUpdateDocumentTypeProperty] = useMutation(ADD_UPDATE_DOCUMENT_TYPE_PROPERTY, {
    onError: () => {
      dispatch({ type: 'onHeaderSaveFailed' });
    }
  });

  const [removeDocumentTypeProperty] = useMutation(REMOVE_DOCUMENT_TYPE_PROPERTY);

  // #endregion

  // #region Subscriptions

  useSubscription(DOCUMENT_TYPE_PROPERTY_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('DocumentTypes.DocumentTypeProperty.DocumentTypePropertyUpdated'), 'success');
      if (propertyId !== undefined && propertyId !== null) {
        client.cache.evict({
          id: `DocumentTypePropertyItem:{"documentTypeId":${documentTypeId},"propertyId":${propertyId}}`
        });
      }
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'documentTypeProperties' });
      client.cache.gc();
      dispatch({ type: 'onHeaderSaved' });
      onRedirect(data.documentTypePropertyUpdated.propertyId);
    }
  });

  // #endregion

  // #region Handlers

  const handlePropertyChanged = (propertyName, value) => {
    dispatch({ type: 'onHeaderPropertyChanged', propertyName, value });
  };

  const handleSave = () => {
    if (!validateDocumentTypeXProperty(localState.documentTypeXProperty)) return;
    dispatch({ type: 'onHeaderSaveStarted' });
    // eslint-disable-next-line no-unused-vars
    const { changed, saving, propertyCode, propertyName, propertyTypeId, ...docTypeXProperty } = localState.documentTypeXProperty;

    if (propertyId !== undefined && propertyId !== null && propertyId !== docTypeXProperty.propertyId) {
      removeDocumentTypeProperty({
        variables: {
          input: { documentTypeId, propertyId }
        }
      });
    }
    return addOrUpdateDocumentTypeProperty({
      variables: { input: { ...docTypeXProperty, defaultValue: docTypeXProperty.defaultValue?.toString() } }
    });
  };

  // #endregion

  // #region Side effects

  const onLanguageChange = useCallback(() => {
    refetchPropertiesData();
    refetchPropertyTypes();
  }, [refetchPropertiesData, refetchPropertyTypes]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  useEffect(() => {
    validateDocumentTypeXProperty(localState.documentTypeXProperty);
  }, [localState.documentTypeXProperty, validateDocumentTypeXProperty]);

  useEffect(() => {
    if (data) dispatch({ type: 'onInitState', data: { ...data, documentTypeId } });
  }, [data, documentTypeId]);

  useEffect(() => {
    onChangeDocumentTypePropertyTitle(data?.propertyName);
  }, [data?.propertyName, onChangeDocumentTypePropertyTitle]);

  // #endregion

  return (
    <DocumentTypePropertyGeneralDataCard
      documentTypeXProperty={localState.documentTypeXProperty}
      properties={propertiesData ? propertiesData.properties : []}
      propertyTypes={propertyTypesData ? propertyTypesData.propertyTypes : []}
      propertyDetails={propertyDetailsData ? propertyDetailsData.dictionaryPropertyDetails : []}
      onSave={handleSave}
      onPropertyChanged={handlePropertyChanged}
      validation={documentTypeXPropertyValidation}
    />
  );
};

DocumentTypePropertyGeneralDataContainer.propTypes = {
  documentTypeId: PropTypes.number,
  propertyId: PropTypes.number,
  data: PropTypes.object,
  onChangeDocumentTypePropertyTitle: PropTypes.func,
  onRedirect: PropTypes.func
};

export default DocumentTypePropertyGeneralDataContainer;
